/* «2. Кто повторит» / «2. Кем стать» (§20.1 п. 5, общий ряд лиц BotPeople §4.4, экран на одного): «Вы», вариант «Вы», близкие, «＋ Добавить»,
   черта, персонажи каталога (персонажи входа — первыми), «Все ›» — полный каталог персонажей, разовое фото «＋ Фото» (сразу кадр).
   Одно касание выбирает, повторное снимает (у разового фото — снова кадр). Питомец бледный («не повторит движения»); близкий без фото —
   жёлтая точка и «Добавьте фото». Под рядом — подпись-правило со щитом (согласие consent_accepted). Ключи: cast или photo (+ crop_x, crop_y, zoom). */
import { useState, type ReactNode } from 'react';
import { uiText } from '../../i18n';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { ToolIcon } from '../studio/circle/ToolIcon';
import { StudioSheet } from '../studio/StudioSheet';
import { artworkUrl } from '../studio/studio-art-model';
import { resetHeroCrop, type MotionWords } from './motion-model';
import type { MotionChoice } from './use-motion-cast';
import { MotionConsent } from './MotionConsent';
import '../../legacy/bot-people.css';

const ROW_CHARS = 4;
export const PHOTO_HINT = 'Нажмите на фото ещё раз: кадр или другое фото';
function Face({
  name,
  image,
  on,
  disabled,
  dot,
  title,
  onClick,
  children,
}: {
  name: string;
  image?: string | null;
  on: boolean;
  disabled: boolean;
  dot?: boolean;
  title?: string;
  onClick(): void;
  children?: ReactNode;
}) {
  const src = artworkUrl(image ?? null);
  return (
    <button
      type="button"
      className={`ps-fc${on ? ' on' : ''}${disabled ? ' off' : ''}`}
      aria-pressed={on}
      disabled={disabled}
      title={title ? uiText(title) : undefined}
      onClick={onClick}
    >
      <span className="ps-fo">
        <span className="ps-fi" aria-hidden="true">
          {children ?? (src ? <img src={src} alt="" /> : <span className="ini">{name.slice(0, 1)}</span>)}
        </span>
        {dot && <i className="motion-dot" aria-hidden="true" />}
        <i className="ck" aria-hidden="true">
          <ToolIcon kind="check" />
        </i>
      </span>
      <b>{name}</b>
    </button>
  );
}
export function MotionHero({
  state,
  words,
  choices,
  error,
  onRetry,
  photoUrl,
  onPhotoFile,
  onPhotoAgain,
}: {
  state: CircleFormState;
  words: MotionWords;
  choices: MotionChoice[] | null;
  error: boolean;
  onRetry(): void;
  /** Превью разового фото (локальное или загруженное) */
  photoUrl: string;
  /** Выбрано новое фото: экран открывает кадр */
  onPhotoFile(file: File): void;
  /** Повторное нажатие на круг «Фото»: снова кадр */
  onPhotoAgain(): void;
}) {
  const [all, setAll] = useState(false),
    [query, setQuery] = useState('');
  const photo = state.form?.fields.find(f => f.key === 'photo' && f.upload?.kind === 'photo');
  const cast = state.inputs.cast ?? '';
  const photoOn = !!state.inputs.photo;
  const locked = state.locked || !choices;
  const pick = (value: string) =>
    state.setInputs(v => {
      const next = resetHeroCrop(v);
      if (next.cast === value) delete next.cast;
      else next.cast = value;
      return next;
    });
  const pickChoice = (choice: MotionChoice) => {
    if (photoOn) state.clearUpload('photo');
    pick(choice.value);
  };
  const own = (choices ?? []).filter(c => c.kind !== 'char');
  const chars = (choices ?? []).filter(c => c.kind === 'char');
  const selectedChar = chars.find(c => c.value === cast);
  const rowChars = selectedChar && !chars.slice(0, ROW_CHARS).includes(selectedChar) ? [selectedChar, ...chars.slice(0, ROW_CHARS - 1)] : chars.slice(0, ROW_CHARS);
  const card = (choice: MotionChoice) => (
    <Face
      key={choice.value}
      name={choice.name}
      image={choice.image}
      on={!photoOn && cast === choice.value}
      disabled={locked || choice.disabled}
      dot={choice.needsPhoto}
      title={choice.reason ?? (choice.needsPhoto ? 'Добавьте фото' : undefined)}
      onClick={() => pickChoice(choice)}
    />
  );
  return (
    <fieldset className="motion-hero">
      <legend className="sr-only">{uiText(words.heroLabel)}</legend>
      <div className="glbl ps-fl">
        <span className="pp-lt">{uiText(words.heroLabel)}</span>
        {photo && <span className="pp-ln">{uiText('можно своё фото')}</span>}
        <a className="pp-chg" href="#profile">
          <ToolIcon kind="edit" />
          {uiText('Изменить')}
        </a>
      </div>
      <div className="ps-fh motion-hero-sub">
        {uiText('Вы, близкий или персонаж')} ·{' '}
        <button type="button" className="lnk" disabled={locked || !chars.length} onClick={() => setAll(true)}>
          {uiText('Персонажи')} ›
        </button>
      </div>
      {!choices && !error && <p role="status">{uiText('Загружаем…')}</p>}
      {error ? (
        <p role="alert">
          {uiText('Не удалось загрузить людей и персонажей.')}{' '}
          <button type="button" onClick={onRetry}>
            {uiText('Повторить')}
          </button>
        </p>
      ) : (
        <div className="ps-face studio-native-people" id="dnPpl">
          <div className="ps-fr" role="group" aria-label={uiText(words.sheet)}>
            {own.map(card)}
            <a className="ps-fc motion-add" href="#profile">
              <span className="ps-fo">
                <span className="ps-fi" aria-hidden="true">
                  <span className="ini">
                    <ToolIcon kind="plus" />
                  </span>
                </span>
              </span>
              <b>{uiText('Добавить')}</b>
            </a>
            {!!chars.length && <span className="ps-fsep" aria-hidden="true" />}
            {rowChars.map(card)}
            {chars.length > ROW_CHARS && (
              <button type="button" className="ps-fc" disabled={locked} onClick={() => setAll(true)}>
                <span className="ps-fo">
                  <span className="ps-fi" aria-hidden="true">
                    <span className="ini">
                      <ToolIcon kind="chev" />
                    </span>
                  </span>
                </span>
                <b>{uiText('Все')}</b>
              </button>
            )}
            {photo &&
              (photoUrl || photoOn ? (
                <Face
                  name={uiText('Фото')}
                  image={photoUrl || null}
                  on={photoOn}
                  disabled={state.locked}
                  onClick={onPhotoAgain}
                >
                  {photoUrl ? <img src={photoUrl} alt="" /> : undefined}
                </Face>
              ) : (
                <label className={`ps-fc motion-ph${state.locked ? ' off' : ''}`}>
                  <span className="ps-fo">
                    <span className="ps-fi" aria-hidden="true">
                      <span className="ini">
                        <ToolIcon kind="plus" />
                      </span>
                    </span>
                  </span>
                  <b>{uiText('Фото')}</b>
                  <input
                    type="file"
                    hidden
                    aria-label={uiText('Своё фото')}
                    accept={photo.upload!.accept.join(',')}
                    disabled={state.locked}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) onPhotoFile(file);
                    }}
                  />
                </label>
              ))}
          </div>
        </div>
      )}
      {photoOn && <p className="ps-fh">{uiText(PHOTO_HINT)}</p>}
      <MotionConsent state={state} />
      {all && (
        <StudioSheet title="Персонажи" onClose={() => setAll(false)}>
          <h2>{uiText(words.heroLabel)}</h2>
          <label className="search">
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={uiText('Поиск')} aria-label={uiText('Поиск')} />
          </label>
          <div className="studio-native-person-grid">
            {chars
              .filter(c => c.name.toLocaleLowerCase().includes(query.toLocaleLowerCase()))
              .map(choice => (
                <Face
                  key={choice.value}
                  name={choice.name}
                  image={choice.image}
                  on={!photoOn && cast === choice.value}
                  disabled={state.locked}
                  onClick={() => {
                    if (photoOn) state.clearUpload('photo');
                    state.setInputs(v => ({ ...resetHeroCrop(v), cast: choice.value }));
                    setAll(false);
                  }}
                />
              ))}
          </div>
        </StudioSheet>
      )}
    </fieldset>
  );
}
